import { ref, readonly } from 'vue'

interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  message: string
  duration?: number
}

const toasts = ref<Toast[]>([])

let toastId = 0

export function useToast() {
  const addToast = (toast: Omit<Toast, 'id'>) => {
    const id = `toast-${++toastId}`
    const newToast: Toast = {
      id,
      duration: 3000,
      ...toast
    }
    
    toasts.value.push(newToast)
    
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, newToast.duration)
    }
    
    return id
  }
  
  const removeToast = (id: string) => {
    const index = toasts.value.findIndex(toast => toast.id === id)
    if (index > -1) {
      toasts.value.splice(index, 1)
    }
  }
  
  const success = (message: string, duration?: number) => {
    return addToast({ type: 'success', message, duration })
  }
  
  const error = (message: string, duration?: number) => {
    return addToast({ type: 'error', message, duration })
  }
  
  const warning = (message: string, duration?: number) => {
    return addToast({ type: 'warning', message, duration })
  }
  
  const info = (message: string, duration?: number) => {
    return addToast({ type: 'info', message, duration })
  }
  
  const clear = () => {
    toasts.value = []
  }
  
  return {
    toasts: readonly(toasts),
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
    clear
  }
}

// Global toast instance
export const toast = {
  success: (message: string, duration?: number) => useToast().success(message, duration),
  error: (message: string, duration?: number) => useToast().error(message, duration),
  warning: (message: string, duration?: number) => useToast().warning(message, duration),
  info: (message: string, duration?: number) => useToast().info(message, duration)
}